import DateFnsUtils from '@date-io/date-fns';
import { DatePicker as MaterialUiPicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ButtonWrapper from '../../../../components/Button/ButtonWrapper';
import DefaultButton from '../../../../components/Button/DefaultButton';
import DetailWrapper from '../../../../components/CardRow/DetailWrapper';
import SectionWrapper from '../../../../components/CardRow/SectionWrapper';
import CustomCheckBox from '../../../../components/CustomizedComponents/CustomCheckBox';
import CustomSnackbar from '../../../../components/CustomizedComponents/CustomSnackbar';
import CustomTextField from '../../../../components/CustomizedComponents/CustomTextField';
import DataTable from '../../../../components/DataTable/DataTable';
import deepCompareObjects from '../../../../utils/deepCompareObjects';
import {
  useFindNotificationImpulses,
  useGetMobileCardConfiguration,
  usePutMobileCardConfiguration,
  useSendMobileNotificationsManually,
} from '../../hooks/api/configurationApi';
import WalletNotificationImpulsColumns from '../../enums/columns';
import { Box } from '@material-ui/core';

const cancel = 'app.buttons.cancel';
const save = 'app.buttons.save';
const sendBulkNotifications = 'app.configuration.sendBulkNotifications';

const MobileCardConfiguration = ({ editable, setEditable }) => {
  const intl = useIntl();
  const { getMobileCardConfiguration, data: mobileCardConfigurationData } = useGetMobileCardConfiguration();
  const { putMobileCardConfiguration, error: putMobileCardConfigurationError, loading: putMobileCardConfigurationLoading } = usePutMobileCardConfiguration();
  const { sendMobileNotifications, loading: sendingManualNotifications } = useSendMobileNotificationsManually();
  const { postFindNotificationImpulses, data: sentNotificationData, error, loading } = useFindNotificationImpulses();

  const success = intl.formatMessage({ id: 'app.action.saved' });
  const dateFormat = intl.messages['app.dateFormat'];
  const maxNoteLengthPlaceholder = intl.formatMessage({ id: 'app.configuration.maxNoteLength' });

  const [configurationState, setConfigurationState] = useState({});
  const [message, setMessage] = useState(success);
  const [open, setOpen] = useState(false);
  const [issues, setIssues] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const handleUpdateConfigurationValue = (key, value) => setConfigurationState((prevState) => ({ ...prevState, [key]: value }));

  // API request for configuration data
  useEffect(() => {
    if (!mobileCardConfigurationData) getMobileCardConfiguration();
    else setConfigurationState(mobileCardConfigurationData);
  }, [getMobileCardConfiguration, mobileCardConfigurationData]);

  useEffect(() => {
    postFindNotificationImpulses({ page: page, size: pageSize });
  }, [postFindNotificationImpulses, page, pageSize]);

  useEffect(() => {
    if (putMobileCardConfigurationError) {
      setMessage(putMobileCardConfigurationError?.data?.message);
      setIssues(putMobileCardConfigurationError?.data?.issues);
    } else {
      setMessage(success);
      setIssues(null);
    }
  }, [success, putMobileCardConfigurationError]);

  return (
    <DetailWrapper>
      <SectionWrapper>
        <FormattedMessage id={'app.configuration.sendingNotificationEnabled'} />
        <CustomCheckBox
          checked={configurationState?.isNotificationEnabled}
          disabled={!editable}
          onChange={(e) => handleUpdateConfigurationValue('isNotificationEnabled', e.target.checked)}
        />
        <FormattedMessage id={configurationState?.isNotificationEnabled ? 'app.configuration.activated' : 'app.configuration.deactivated'} />
      </SectionWrapper>
      <CustomTextField
        value={configurationState?.note ?? ''}
        onChange={(e) => handleUpdateConfigurationValue('note', e.target.value)}
        label={<FormattedMessage id={'app.configuration.note'} />}
        placeholder={maxNoteLengthPlaceholder}
        disabled={!editable}
        variant="outlined"
        inputProps={{
          maxLength: 256,
        }}
      />
      <SectionWrapper>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <MaterialUiPicker
            style={{ width: '55%' }}
            inputVariant="outlined"
            variant="inline"
            label={<FormattedMessage id={'app.configuration.newMobileCardsExpiration'} />}
            format={dateFormat}
            autoOk
            disabled={!editable}
            disableToolbar
            value={configurationState?.expirationDate}
            onChange={(date) => handleUpdateConfigurationValue('expirationDate', date)}
          />
        </MuiPickersUtilsProvider>
      </SectionWrapper>
      <DefaultButton primary disabled={sendingManualNotifications} title={sendBulkNotifications} onClick={sendMobileNotifications} />
      <Box marginTop={2} marginBottom={2}>
        <DataTable
          displayToolbar={false}
          columns={WalletNotificationImpulsColumns}
          setPage={setPage}
          rowsPerPageOptions={[10, 25, 50]}
          rowsPerPage={pageSize}
          setRowsPerPage={setPageSize}
          page={page}
          totalCount={sentNotificationData?.totalCount ?? 0}
          data={sentNotificationData?.data ?? []}
          loading={loading}
          error={error}
        />
      </Box>
      <ButtonWrapper>
        <DefaultButton primary={false} disabled={!editable} title={cancel} onClick={() => setEditable(!editable)} />
        <DefaultButton
          primary
          disabled={!editable}
          type="submit"
          title={save}
          loaders={[putMobileCardConfigurationLoading]}
          onClick={() => {
            if (!deepCompareObjects(mobileCardConfigurationData, configurationState)) {
              const newDate = new Date(configurationState.expirationDate);
              const dateWithCorrectTimezone = new Date(newDate.getTime() + newDate.getTimezoneOffset() * 60000);
              putMobileCardConfiguration({
                ...configurationState,
                expirationDate: format(dateWithCorrectTimezone, `yyyy-MM-dd'T'HH:mm:ss'+0000'`),
                note: configurationState.note.length ? configurationState.note : null,
              });
              setOpen(true);
              setEditable(false);
            }
          }}
        />
      </ButtonWrapper>
      {!putMobileCardConfigurationLoading && message && (
        <CustomSnackbar status={!putMobileCardConfigurationError ? 'success' : 'error'} message={message} setOpen={setOpen} open={open} />
      )}
      {!putMobileCardConfigurationLoading &&
        issues &&
        issues.map((issue) => <CustomSnackbar key={issue} status={'error'} message={issue} setOpen={setOpen} open={open} />)}
    </DetailWrapper>
  );
};

MobileCardConfiguration.propTypes = {
  editable: PropTypes.bool,
  setEditable: PropTypes.func,
};

export default MobileCardConfiguration;
